import * as Sentry from '@sentry/react';
import { PORTAL_NAME } from '@admin-panel/config/env';

declare const __SENTRY_ENVIRONMENT__: string | null;

const SENTRY_ENABLED_PORTALS = new Set(['admin', 'synq', 'vendor', 'warehouse']);
const NOISY_CLIENT_ERROR_PATTERNS = [
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /Non-Error promise rejection captured/i,
  /AbortError/i,
  /NetworkError when attempting to fetch resource/i,
];

const sentryEnvironment = (__SENTRY_ENVIRONMENT__ ?? import.meta.env.VITE_APP_ENV ?? '').toLowerCase();

export const shouldUseSentry =
  SENTRY_ENABLED_PORTALS.has(PORTAL_NAME) &&
  sentryEnvironment === 'prod' &&
  Boolean(import.meta.env.VITE_SENTRY_DSN);

export const shouldDropNoisyClientError = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
) => {
  const candidateMessages = [
    event.message,
    ...(event.exception?.values?.map(exceptionValue => exceptionValue.value) ?? []),
    hint.originalException instanceof Error ? hint.originalException.message : null,
  ].filter(Boolean);

  return candidateMessages.some(message =>
    NOISY_CLIENT_ERROR_PATTERNS.some(pattern => pattern.test(message as string)),
  );
};

export const buildSentryTracePropagationTargets = (
  ...urls: Array<string | null | undefined>
): Array<string> => {
  const tracePropagationTargets = new Set<string>(['localhost']);

  for (const url of urls) {
    if (!url) {
      continue;
    }

    try {
      tracePropagationTargets.add(new URL(url).origin);
    } catch {
      // Ignore invalid URLs in local env config.
    }
  }

  return Array.from(tracePropagationTargets);
};
